import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent } from './ui/card';
import { Badge } from './ui/badge';
import { Phone, MessageCircle, Trash2, Users, Route, Car, Search } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const OPTS = { withCredentials: true };

const STATUS_STYLES = {
  new: 'bg-blue-100 text-blue-800 border-blue-200',
  contacted: 'bg-amber-100 text-amber-800 border-amber-200',
  converted: 'bg-green-100 text-green-800 border-green-200',
  lost: 'bg-gray-100 text-gray-600 border-gray-200',
};

const formatInr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;
const formatDate = (iso) =>
  new Date(iso).toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const waLink = (phone) => `https://wa.me/${phone.replace(/\D/g, '').replace(/^(\d{10})$/, '91$1')}`;

const StatCard = ({ label, value, testId }) => (
  <Card className="border-gray-200">
    <CardContent className="p-4">
      <p className="text-xs uppercase tracking-wide text-gray-500">{label}</p>
      <p data-testid={testId} className="text-2xl font-bold text-gray-900 mt-1">{value}</p>
    </CardContent>
  </Card>
);

const LeadCard = ({ lead, onStatus, onDelete }) => (
  <Card data-testid={`lead-card-${lead.id}`} className="border-gray-200 hover:border-gray-300 transition-colors">
    <CardContent className="p-4 sm:p-5">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold text-gray-900 truncate">{lead.name || 'Unknown caller'}</h3>
            <Badge data-testid={`lead-status-${lead.id}`} className={`border ${STATUS_STYLES[lead.status] || STATUS_STYLES.new}`}>
              {lead.status}
            </Badge>
          </div>
          <a href={`tel:${lead.phone}`} data-testid={`lead-phone-${lead.id}`} className="text-sm text-gray-700 hover:underline">{lead.phone}</a>
          <div className="mt-2 flex items-start gap-2 text-sm text-gray-700">
            <Route className="w-4 h-4 mt-0.5 text-gray-400 flex-shrink-0" />
            <span className="break-words">{lead.route}</span>
          </div>
          <div className="mt-1 flex items-center gap-4 text-xs text-gray-500 flex-wrap">
            <span className="flex items-center gap-1"><Car className="w-3.5 h-3.5" />{lead.vehicle_type} · {lead.trip_type === 'roundtrip' ? `Round trip · ${lead.days} day(s)` : 'One way'}</span>
            <span>{lead.total_km} km</span>
            <span>{formatDate(lead.created_at)}</span>
          </div>
        </div>
        <div className="text-left sm:text-right flex-shrink-0">
          <p className="text-xs text-gray-500">Quoted</p>
          <p data-testid={`lead-fare-${lead.id}`} className="text-xl font-bold text-gray-900">{formatInr(lead.total_estimate)}</p>
        </div>
      </div>
      <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-gray-100 pt-3">
        <a href={`tel:${lead.phone}`} data-testid={`lead-call-${lead.id}`} className="inline-flex items-center gap-1.5 rounded-md bg-gray-900 text-white text-xs font-medium px-3 py-1.5 hover:bg-gray-800">
          <Phone className="w-3.5 h-3.5" /> Call
        </a>
        <a href={waLink(lead.phone)} target="_blank" rel="noreferrer" data-testid={`lead-whatsapp-${lead.id}`} className="inline-flex items-center gap-1.5 rounded-md bg-green-600 text-white text-xs font-medium px-3 py-1.5 hover:bg-green-700">
          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
        </a>
        <select
          data-testid={`lead-status-select-${lead.id}`}
          value={lead.status}
          onChange={(e) => onStatus(lead.id, e.target.value)}
          className="ml-auto rounded-md border border-gray-300 bg-white text-xs px-2 py-1.5 text-gray-700"
        >
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="converted">Converted</option>
          <option value="lost">Lost</option>
        </select>
        <button data-testid={`lead-delete-${lead.id}`} onClick={() => onDelete(lead.id)} className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-red-50" aria-label="Delete lead">
          <Trash2 className="w-4 h-4" />
        </button>
      </div>
    </CardContent>
  </Card>
);

export const LeadsPage = () => {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [query, setQuery] = useState('');

  const fetchLeads = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/leads`, OPTS);
      setLeads(res.data);
    } catch {
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchLeads(); }, [fetchLeads]);

  const updateStatus = async (id, status) => {
    try {
      const res = await axios.patch(`${BACKEND_URL}/api/leads/${id}`, { status }, OPTS);
      setLeads((prev) => prev.map((l) => (l.id === id ? res.data : l)));
      toast.success(`Marked as ${status}`);
    } catch {
      toast.error('Failed to update lead');
    }
  };

  const deleteLead = async (id) => {
    if (!window.confirm('Delete this lead?')) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/leads/${id}`, OPTS);
      setLeads((prev) => prev.filter((l) => l.id !== id));
      toast.success('Lead deleted');
    } catch {
      toast.error('Failed to delete lead');
    }
  };

  const q = query.trim().toLowerCase();
  const visible = leads.filter((l) =>
    (filter === 'all' || l.status === filter) &&
    (!q || [l.name, l.phone, l.route].some((v) => (v || '').toLowerCase().includes(q)))
  );
  const count = (s) => leads.filter((l) => l.status === s).length;

  return (
    <div data-testid="leads-page" className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard label="Total Leads" value={leads.length} testId="leads-total" />
        <StatCard label="New" value={count('new')} testId="leads-new" />
        <StatCard label="Contacted" value={count('contacted')} testId="leads-contacted" />
        <StatCard label="Converted" value={count('converted')} testId="leads-converted" />
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            data-testid="leads-search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, phone or route"
            className="w-full rounded-md border border-gray-300 pl-9 pr-3 py-2 text-sm"
          />
        </div>
        <select data-testid="leads-filter" value={filter} onChange={(e) => setFilter(e.target.value)} className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm">
          <option value="all">All statuses</option>
          <option value="new">New</option>
          <option value="contacted">Contacted</option>
          <option value="converted">Converted</option>
          <option value="lost">Lost</option>
        </select>
      </div>

      {loading ? (
        <p className="text-sm text-gray-500 text-center py-12">Loading leads…</p>
      ) : visible.length === 0 ? (
        <div data-testid="leads-empty" className="text-center py-16 text-gray-500">
          <Users className="w-10 h-10 mx-auto mb-3 text-gray-300" />
          <p className="font-medium">No leads {filter !== 'all' ? `marked ${filter}` : 'yet'}</p>
          <p className="text-sm">Fare enquiries with a phone number from the website will appear here.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {visible.map((lead) => (
            <LeadCard key={lead.id} lead={lead} onStatus={updateStatus} onDelete={deleteLead} />
          ))}
        </div>
      )}
    </div>
  );
};
